import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../services/clinical-onboarding.service';
import { ToastService } from '../../services/general/toast.service';
import { ClinicianDocumentTemplate, ClinicianDocumentTemplateScope } from '../../models/clinical-onboarding.model';

/**
 * Ridisegno "documenti flessibili" (feedback utente: titolo libero, illimitati,
 * "generici oppure del solo clinico", "sola presa visione oppure restituzione
 * file"): crea o modifica un ClinicianDocumentTemplate. Ambito (scope) non e'
 * modificabile in edit - cambia dove vivono i file delle righe candidato gia'
 * esistenti, stesso vincolo del backend (updateDocumentTemplate).
 */
@Component({
  selector: 'app-modal-manage-clinical-onboarding-document-template',
  templateUrl: './modal-manage-clinical-onboarding-document-template.component.html',
  styleUrls: ['./modal-manage-clinical-onboarding-document-template.component.scss'],
  standalone: false,
})
export class ModalManageClinicalOnboardingDocumentTemplateComponent {
  loading = false;
  form: FormGroup;
  file: File | null = null;
  readonly ClinicianDocumentTemplateScope = ClinicianDocumentTemplateScope;
  readonly isEdit: boolean;

  constructor(
    public dialogRef: MatDialogRef<ModalManageClinicalOnboardingDocumentTemplateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string | number; template?: ClinicianDocumentTemplate },
    private fb: FormBuilder,
    private clinicalOnboardingService: ClinicalOnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.isEdit = !!data.template;

    this.form = this.fb.group({
      title: [data.template?.title || '', Validators.required],
      scope: [data.template?.scope || ClinicianDocumentTemplateScope.GENERIC, Validators.required],
      requires_upload: [data.template?.requires_upload ?? false],
      visible_only_if_employer_public: [data.template?.visible_only_if_employer_public ?? false],
      counts_as_invoice: [data.template?.counts_as_invoice ?? false],
    });

    // "Conta come fattura" ha senso solo per un documento per-clinico con
    // restituzione file (stesso vincolo lato server) - lo tengo sincronizzato qui
    // cosi' l'utente non arriva mai a un invio che il backend rifiuterebbe.
    this.form.get('scope').valueChanges.subscribe(() => this.syncCountsAsInvoiceAvailability());
    this.form.get('requires_upload').valueChanges.subscribe(() => this.syncCountsAsInvoiceAvailability());
  }

  private syncCountsAsInvoiceAvailability(): void {
    if (!this.countsAsInvoiceAvailable) {
      this.form.get('counts_as_invoice').setValue(false, { emitEvent: false });
    }
  }

  get countsAsInvoiceAvailable(): boolean {
    return this.form.get('scope').value === ClinicianDocumentTemplateScope.PER_CANDIDATE && !!this.form.get('requires_upload').value;
  }

  get isGeneric(): boolean {
    return this.form.get('scope').value === ClinicianDocumentTemplateScope.GENERIC;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file = input.files?.[0] || null;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isGeneric && !this.isEdit && !this.file) {
      this.toastService.error(
        this.translateService.instant('generic.error'),
        this.translateService.instant('components.clinical-onboarding.document-templates.file-required'),
      );
      return;
    }

    this.loading = true;
    const value = this.form.value;

    if (this.isEdit) {
      this.clinicalOnboardingService.updateDocumentTemplate(
        this.data.surveyId,
        this.data.template.id,
        {
          title: value.title,
          requires_upload: value.requires_upload,
          visible_only_if_employer_public: value.visible_only_if_employer_public,
          counts_as_invoice: value.counts_as_invoice,
          file: this.file,
        },
        () => this.onSaved(),
        () => (this.loading = false),
      );
    } else {
      this.clinicalOnboardingService.createDocumentTemplate(
        this.data.surveyId,
        {
          title: value.title,
          scope: value.scope,
          requires_upload: value.requires_upload,
          visible_only_if_employer_public: value.visible_only_if_employer_public,
          counts_as_invoice: value.counts_as_invoice,
          file: this.file,
        },
        () => this.onSaved(),
        () => (this.loading = false),
      );
    }
  }

  private onSaved(): void {
    this.loading = false;
    this.toastService.success(
      this.translateService.instant('generic.success'),
      this.translateService.instant('components.clinical-onboarding.document-templates.saved'),
    );
    this.dialogRef.close(true);
  }

  closeDialogue(): void {
    this.dialogRef.close();
  }
}
